import { useAuth0 } from '@auth0/auth0-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useProfileName } from '../hooks/useProfileName'
import { auth0AvatarBackground } from '../utils/auth0AvatarColor'
import { deriveAvatarInitials } from '../utils/displayName'

const HOME_PATH = '/home'

const linkClassName = ({ isActive }: { isActive: boolean }) => {
  return [
    'rounded-lg px-3 py-2 text-sm font-medium block w-full text-left sm:block sm:w-auto',
    isActive
      ? 'bg-blue-50 text-blue-700 dark:bg-brand-600/20 dark:text-brand-400'
      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white',
  ].join(' ')
}

const userMenuItemClassName =
  'block w-full rounded-md px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-blue-50 hover:text-blue-700 focus-visible:bg-blue-50 focus-visible:outline-none dark:text-gray-300 dark:hover:bg-brand-600/20 dark:hover:text-brand-400 dark:focus-visible:bg-brand-600/20'

const userMenuPanelClassName =
  'absolute top-full right-0 z-50 min-w-40 overflow-hidden rounded-xl border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-800'

// Fill comes from auth0AvatarBackground (inline). White glyph matches Auth0 CDN
// letter avatars. Avoid gray-* / fixed brand fill — color must follow initials.
const userAvatarButtonClassName =
  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-transparent text-sm font-medium text-white'

// Mid-gray + border so the pulse reads on the white header (gray-200 alone looks blank).
const userAvatarSkeletonClassName =
  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-gray-400 bg-gray-300 animate-pulse dark:border-gray-400 dark:bg-gray-600'

type UserAvatarButtonProps = {
  initials: string
  isLoading: boolean
  ariaLabel: string
  ariaExpanded: boolean
  onClick: () => void
}

function UserAvatarButton({ initials, isLoading, ariaLabel, ariaExpanded, onClick }: UserAvatarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={isLoading ? userAvatarSkeletonClassName : userAvatarButtonClassName}
      style={isLoading ? undefined : { backgroundColor: auth0AvatarBackground(initials) }}
      aria-expanded={ariaExpanded}
      aria-haspopup="true"
      aria-label={ariaLabel}
      aria-busy={isLoading || undefined}
      data-state={isLoading ? 'loading' : undefined}
    >
      {isLoading ? (
        <span aria-hidden className="h-3.5 w-3.5 rounded-full bg-gray-500 dark:bg-gray-300" />
      ) : (
        <span aria-hidden>{initials}</span>
      )}
    </button>
  )
}

const Navigation: React.FC = () => {
  const intl = useIntl()
  const { logout } = useAuth0()
  const { isLoading: isProfileLoading, ...profileName } = useProfileName()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const userMenuDesktopRef = useRef<HTMLDivElement>(null)
  const userMenuMobileRef = useRef<HTMLDivElement>(null)
  const location = useLocation()

  const homeLabel = intl.formatMessage({ id: 'nav.links.home', defaultMessage: 'Home' })
  const navLinks = [
    {
      to: HOME_PATH,
      label: homeLabel,
    },
  ]

  const closeMenu = useCallback(() => setIsMenuOpen(false), [])
  const closeUserMenu = useCallback(() => setIsUserMenuOpen(false), [])

  const initials = deriveAvatarInitials(profileName)
  const userMenuLabel = intl.formatMessage({ id: 'nav.user.menuLabel', defaultMessage: 'User menu' })
  useEffect(() => {
    closeMenu()
  }, [location.pathname, closeMenu])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeMenu()
        closeUserMenu()
      }
    }
    if (isMenuOpen || isUserMenuOpen) window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isMenuOpen, isUserMenuOpen, closeMenu, closeUserMenu])

  const isClickedOpenRef = useRef(false)

  useEffect(() => {
    // If the menu gets closed by other means (like pressing Escape), reset our flag
    if (!isUserMenuOpen) {
      isClickedOpenRef.current = false
    }

    const handleMouseEnter = () => {
      // Hover only applies if we haven't already clicked it open
      if (!isClickedOpenRef.current) {
        setIsUserMenuOpen(true)
      }
    }

    const handleMouseLeave = () => {
      // If clicked, hover conditions do not apply; stay open. Otherwise, hide.
      if (!isClickedOpenRef.current) {
        setIsUserMenuOpen(false)
      }
    }

    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as Node

      // Handle the main layout menu close
      if (menuRef.current && !menuRef.current.contains(target)) {
        closeMenu()
      }

      const inDesktop = userMenuDesktopRef.current?.contains(target)
      const inMobile = userMenuMobileRef.current?.contains(target)

      if (inDesktop) {
        // User clicked inside the desktop container (either avatar or menu item)
        // If they click a link/button inside the dropdown, let normal logic handle it.
        // If they clicked the avatar itself, flip the lock state.
        const isTriggerClick =
          !isUserMenuOpen || (target instanceof HTMLElement && target.closest('button[aria-haspopup="true"]'))

        if (isTriggerClick) {
          isClickedOpenRef.current = !isClickedOpenRef.current
          setIsUserMenuOpen(isClickedOpenRef.current)
        }
      } else if (!inMobile) {
        // User clicked completely outside. Reset flag and hide.
        isClickedOpenRef.current = false
        closeUserMenu()
      }
    }

    // Always listen for global clicks to handle locking and closing outside
    document.addEventListener('mousedown', handleDocumentClick)

    const desktopEl = userMenuDesktopRef.current
    if (desktopEl) {
      desktopEl.addEventListener('mouseenter', handleMouseEnter)
      desktopEl.addEventListener('mouseleave', handleMouseLeave)
    }

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick)
      if (desktopEl) {
        desktopEl.removeEventListener('mouseenter', handleMouseEnter)
        desktopEl.removeEventListener('mouseleave', handleMouseLeave)
      }
    }
  }, [isMenuOpen, isUserMenuOpen, closeMenu, closeUserMenu])

  return (
    <header ref={menuRef} className="border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
      <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-4 px-4 py-3">
        {/* Brand: icon + wordmark */}
        <Link
          to={HOME_PATH}
          aria-label={`Inflection Medicine, ${homeLabel}`}
          className="-m-2 flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 focus-visible:outline-none dark:hover:bg-gray-800"
        >
          <img
            src="/Inflection_Icon_Dark.svg"
            alt=""
            className="h-9 w-9 shrink-0 object-contain"
            width={36}
            height={36}
            aria-hidden
          />
          <img
            src="/Group%201.svg"
            alt=""
            className="h-6 shrink-0 object-contain"
            width={120}
            height={24}
            aria-hidden
          />
        </Link>

        {/* Desktop: nav links + user menu */}
        <nav className="hidden items-center gap-1 sm:flex" aria-label="Primary">
          {navLinks.map(({ to, label }) => (
            <NavLink key={to} to={to} className={linkClassName}>
              {label}
            </NavLink>
          ))}
          <div className="relative" ref={userMenuDesktopRef}>
            <UserAvatarButton
              initials={initials}
              isLoading={isProfileLoading}
              ariaLabel={userMenuLabel}
              ariaExpanded={isUserMenuOpen}
              onClick={() => {
                if (window.innerWidth < 640) {
                  setIsUserMenuOpen((open) => !open)
                }
              }}
            />
            {isUserMenuOpen && (
              <div
                role="menu"
                className={userMenuPanelClassName}
                aria-label={intl.formatMessage({ id: 'nav.user.menuLabel', defaultMessage: 'User menu' })}
              >
                <button
                  type="button"
                  role="menuitem"
                  className={userMenuItemClassName}
                  onClick={() => {
                    closeUserMenu()
                    navigate('/profile')
                  }}
                >
                  {intl.formatMessage({ id: 'nav.user.profile', defaultMessage: 'Profile' })}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className={userMenuItemClassName}
                  onClick={() => {
                    closeUserMenu()
                    logout({ logoutParams: { returnTo: window.location.origin } })
                  }}
                >
                  {intl.formatMessage({ id: 'nav.user.signOut', defaultMessage: 'Sign out' })}
                </button>
              </div>
            )}
          </div>
        </nav>

        {/* Mobile: hamburger + user menu */}
        <div className="flex items-center gap-3 sm:hidden">
          <button
            type="button"
            onClick={() => setIsMenuOpen((open) => !open)}
            className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
          >
            <span className="sr-only">
              {isMenuOpen
                ? intl.formatMessage({ id: 'nav.menu.close', defaultMessage: 'Close menu' })
                : intl.formatMessage({ id: 'nav.menu.open', defaultMessage: 'Open menu' })}
            </span>
            <img src="/bars.svg" alt="" className="h-6 w-6" aria-hidden />
          </button>
          <div className="relative" ref={userMenuMobileRef}>
            <UserAvatarButton
              initials={initials}
              isLoading={isProfileLoading}
              ariaLabel={userMenuLabel}
              ariaExpanded={isUserMenuOpen}
              onClick={() => setIsUserMenuOpen((open) => !open)}
            />
            {isUserMenuOpen && (
              <div
                role="menu"
                className={`${userMenuPanelClassName} mt-2`}
                aria-label={intl.formatMessage({ id: 'nav.user.menuLabel', defaultMessage: 'User menu' })}
              >
                <button
                  type="button"
                  role="menuitem"
                  className={userMenuItemClassName}
                  onClick={() => {
                    closeUserMenu()
                    navigate('/profile')
                  }}
                >
                  {intl.formatMessage({ id: 'nav.user.profile', defaultMessage: 'Profile' })}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className={userMenuItemClassName}
                  onClick={() => {
                    closeUserMenu()
                    logout({ logoutParams: { returnTo: window.location.origin } })
                  }}
                >
                  {intl.formatMessage({ id: 'nav.user.signOut', defaultMessage: 'Sign out' })}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      <div
        id="mobile-menu"
        role="menu"
        aria-label={intl.formatMessage({ id: 'nav.menu.primaryNavLabel', defaultMessage: 'Primary navigation' })}
        className={`sm:hidden ${isMenuOpen ? 'block' : 'hidden'}`}
      >
        <div className="border-t border-gray-200 px-4 py-3 dark:border-gray-700">
          <ul className="flex flex-col gap-1">
            {navLinks.map(({ to, label }) => (
              <li key={to}>
                <NavLink to={to} className={linkClassName} onClick={closeMenu}>
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </header>
  )
}

export default Navigation
